import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import {
    useParams, Redirect,
} from 'react-router-dom';

import { useCategory } from 'features/categorySlice';
import { useActivity } from 'features/activitySlice';
import { useArticle } from 'features/articleSlice';

import { getDescription } from 'utils/getDescription';

import Message from 'components/Utils/Message';
import ArticleSlider from 'components/ArticleSlider';
import CarouselCard from 'components/CarouselCard';
import BasePage from '../BasePage';
import Head from './components/Head';
import Content from './components/Content';
import SubInfo from './components/SubInfo';

import styles from './index.module.scss';

const InnerPage: React.FC = (): JSX.Element => {
    const { listName, id } = useParams<{ listName: string, id: string }>();

    const [{
        categoryList,
        loading: isSearchingCategory,
        fail: searchingCategoryFail,
        success: searchingCategorySuccessfully
    }] = useCategory();

    const [{
        articleDetail: articleDetail1,
        prevArticle,
        nextArticle,
        recommendation
    }, {
        fetchArticleDetail,
        fetchPreviewArticle
    }] = useArticle();

    const [{ activityDetail }, { fetchActivityDetail }] = useActivity();

    const [content, setContent] = useState<IArticleDetail>();
    const [isArticle, setIsArticle] = useState(false);
    const [copied, setCopied] = useState(false);
    const [exist, setExist] = useState(true);
    const [loading, setLoading] = useState(true);

    const setMeta = (title: string, text: string, imageURL: string) => {
        document.title = `${title} | 教育家`;
        document.querySelectorAll('meta[property=og\\:title]')[0].setAttribute('content', `${title} | 教育家`);
        document.querySelectorAll('meta[name=description]')[0].setAttribute('content', getDescription(text));
        document.querySelectorAll('meta[property=og\\:description]')[0].setAttribute('content', getDescription(text));
        document.querySelectorAll('meta[property=og\\:image]')[0].setAttribute('content', getDescription(imageURL));
    };

    const isInitialized = useRef(false);

    // 該頁url不在分類裡，若該url在分類內，fetch文章;activity則fetch活動
    useEffect(() => {
        if (searchingCategorySuccessfully && !isInitialized.current) {
            // 處理分類名
            if (listName === 'preview') {
                setIsArticle(true);
                fetchPreviewArticle(id);
            } else if (_.some(categoryList, ['url', listName])) {
                setIsArticle(true);
                fetchArticleDetail(id);
            } else if (listName === 'activity') {
                fetchActivityDetail(id);
            } else {
                setExist(false);
            }
            isInitialized.current = true;
        }
    }, [categoryList,
        fetchActivityDetail,
        fetchArticleDetail,
        fetchPreviewArticle,
        id,
        listName,
        searchingCategorySuccessfully]);

    useEffect(() => {
        if (!_.isEmpty(activityDetail)) {
            document.title = `${activityDetail.title} | 教育家`;
            setMeta(activityDetail.title, activityDetail.text, activityDetail.imageURL);

            setContent(activityDetail);
            setLoading(false);
        }
    }, [activityDetail]);

    useEffect(() => {
        if (!_.isEmpty(articleDetail1)) {
            if (listName !== articleDetail1.relatedMainCategory?.url && listName !== 'preview') {
                setLoading(false);
                setExist(false);
            } else {
                setMeta(articleDetail1.title, articleDetail1.text, articleDetail1.imageURL);
                setContent(articleDetail1);
                setLoading(false);
            }
        }
    }, [articleDetail1, listName]);

    // 複製連結（三秒後取消）
    useEffect(() => {
        const timeId = setTimeout(() => {
            setCopied(false);
        }, 3000);

        return () => {
            clearTimeout(timeId);
        };
    }, [copied]);

    if (exist && (isSearchingCategory || loading)) {
        return <div />;
    }

    // 倒轉回首頁：1.打不到分類 2.該頁url不在分類裡 3.沒有分類
    if (searchingCategoryFail || !exist || (searchingCategorySuccessfully && categoryList.length === 0)) {
        return <Redirect to="" />;
    }

    return (
        <BasePage categoryList={categoryList}>
            <div className={styles.container}>
                <Head
                    title={content?.title}
                    setCopied={setCopied}
                    isArticle={isArticle}
                    author={isArticle ? content?.author : ''}
                    dataSource={isArticle ? content?.dataSource : ''}
                    imageSource={isArticle ? content?.imageSource : ''}
                    date={isArticle ? content?.publicAt : ''}
                    mainCategory={isArticle ? content?.relatedMainCategory.name : ''}
                    subCategory={isArticle ? content?.relatedSubCategory.name : ''}
                    isPreview={listName === 'preview'}
                />
                {content && <Content content={content?.text} />}
            </div>
            {
                (isArticle && content && content.relatedProjects?.length > 0)
                && content.relatedProjects.map(project => (
                    project.projectArticle && project.projectArticle.articleList.length > 0 && (
                        <ArticleSlider
                            className={styles.topic}
                            title={project.name}
                            list={project.projectArticle.articleList}
                            key={`project-${project.id}`}
                            projectId={`project-${project.id}`}
                            isFromInnerPage
                        />
                    )
                ))
            }
            {
                (isArticle && content && content.images?.length > 0)
                && (
                    <CarouselCard
                        className={styles.carousel}
                        carouselList={content.images}
                        hasCard={false}
                        isFromArticle
                    />
                )
            }
            {
                (isArticle && content && content.imagesDescription)
                && (
                    <div className={styles.photoDescriptionContainer}>
                        <pre className={styles.photoDescription}>
                            {content.imagesDescription}
                        </pre>
                    </div>
                )
            }
            {
                (isArticle && recommendation.length > 0) && (
                    <ArticleSlider
                        className={styles.relatedSubCategory}
                        title="你也可能會喜歡"
                        list={recommendation}
                        projectId="recommand"
                        isFromInnerPage
                    />
                )
            }
            {
                isArticle ? (
                    <SubInfo
                        setCopied={setCopied}
                        tags={content?.relatedKeywords}
                        last={prevArticle?.id}
                        next={nextArticle?.id}
                        isArticle={isArticle}
                        listName={listName}
                        category={articleDetail1.relatedMainCategory.name}
                    />
                ) : (
                    <SubInfo
                        setCopied={setCopied}
                        isArticle={isArticle}
                    />
                )
            }
            {copied && <Message text="已複製連結" />}
            {listName === 'preview' && <div className={styles.previewContainer}><div className={styles.preview}>預覽</div></div> }
        </BasePage>
    );
};

export default InnerPage;
